import styles from './LevelMenu.module.scss';
import { FontAwesomeProIcon, Link } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';
import { Button as BaseButton } from '@ariakit/react';

// buffer for paddings when calculating heights of elements
const BUFFER = 12;

const decideWhetherToShowFadingBackground = (
  wrapperRef,
  menuListRef,
  setDisplayFadingBackground,
) => {
  // show only if content is scrollable and end of scroll height has not been reached
  if (
    wrapperRef.current.clientHeight <
    menuListRef.current.clientHeight + BUFFER
  ) {
    if (
      wrapperRef.current.scrollTop + wrapperRef.current.clientHeight <
      wrapperRef.current.scrollHeight
    ) {
      setDisplayFadingBackground(true);
      return;
    }
  }
  setDisplayFadingBackground(false);
};

const handleScroll = (
  isCurrentLevel,
  wrapperRef,
  menuListRef,
  setDisplayFadingBackground,
) => {
  if (isCurrentLevel) {
    decideWhetherToShowFadingBackground(
      wrapperRef,
      menuListRef,
      setDisplayFadingBackground,
    );
  }
};

const LevelMenu = ({
  level,
  position,
  parentItem,
  makeLevelDecision,
  selectedIndices,
  isFirstLevel,
  displayFadingBackground,
  setDisplayFadingBackground,
  setShowOverlay,
  ariaLabel,
  ariaHidden,
  ...props
}) => {
  const wrapperRef = useRef(null);
  const menuListRef = useRef(null);

  const isCurrentLevel = useMemo(() => level === position, [level, position]);

  useEffect(() => {
    if (!isCurrentLevel) {
      return () => {};
    }
    decideWhetherToShowFadingBackground(
      wrapperRef,
      menuListRef,
      setDisplayFadingBackground,
    );

    const resizeHandler = () => {
      decideWhetherToShowFadingBackground(
        wrapperRef,
        menuListRef,
        setDisplayFadingBackground,
      );
    };

    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [level]);

  if (!parentItem) {
    return null;
  }

  return (
    <div
      aria-hidden={ariaHidden}
      className={styles.wrapper}
      {...props}
      onScroll={() =>
        handleScroll(
          isCurrentLevel,
          wrapperRef,
          menuListRef,
          setDisplayFadingBackground,
        )
      }
      ref={wrapperRef}
    >
      <ul
        aria-label={ariaLabel}
        className={classNames(styles.list, { [styles.show]: isCurrentLevel })}
        ref={menuListRef}
        role="menu"
      >
        {/* hide parent item on first level */}
        {!isFirstLevel && (
          <li
            className={classNames(styles.parentItem, {
              [styles.isFirstLevel]: isFirstLevel,
            })}
          >
            <BaseButton
              aria-label="Zurück zur vorigen Menüebene"
              className={classNames(styles.itemButton, styles.parentItemButton)}
              onClick={() =>
                makeLevelDecision(level - 1, selectedIndices[level - 1])
              }
              role="menuitem"
            >
              <FontAwesomeProIcon
                className={styles.backIcon}
                icon="chevron-left"
                role="presentation"
              />
              {parentItem.title}
            </BaseButton>
          </li>
        )}
        {parentItem.children.map((item, index) => {
          // a maximum of four levels should be displayed
          if (item.children.length !== 0 && level < 3) {
            return (
              <li
                className={classNames({
                  [styles.isFirstLevel]: isFirstLevel,
                })}
                key={item.id}
                role="none"
              >
                <BaseButton
                  aria-label={`Menü ${item.title}`}
                  className={classNames(styles.itemButton, {
                    [styles.isFirstLevel]: isFirstLevel,
                  })}
                  onClick={() => makeLevelDecision(level + 1, index)}
                  role="menuitem"
                >
                  {item.title}
                  <FontAwesomeProIcon
                    icon="chevron-right"
                    role="presentation"
                  />
                </BaseButton>
              </li>
            );
          }
          return (
            <li
              className={classNames({
                [styles.isFirstLevel]: isFirstLevel,
              })}
              key={item.id}
              role="none"
            >
              <Link
                aria-label={
                  item.type === 'overview'
                    ? `Übersichtsseite ${parentItem.title}`
                    : null
                }
                className={classNames(styles.itemLink, {
                  [styles.isFirstLevel]: isFirstLevel,
                })}
                onClick={() => setShowOverlay(false)}
                role="menuitem"
                to={item.url}
              >
                {item.type === 'overview' && (
                  <FontAwesomeProIcon icon="grid" role="presentation" />
                )}
                {item.title}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LevelMenu;
